import React from "react";
import { comprobarVersion, esErrorDeChunk, manejarErrorDeChunk } from "./version-nueva";

/**
 * Carga diferida de código que no rompe la vista si la red falla (#1717 R3, R4, R6).
 *
 * Por qué no alcanza con `React.lazy(() => import(...))` a secas: si el chunk no llega, el
 * rechazo lo atrapa React y sube como error de render. No pasa por `unhandledrejection` ni por
 * el `error` de window, así que la recarga de `version-nueva.js` (R4 de #1451) nunca se entera
 * y la vista queda en blanco. Acá se resuelve en dos pasos:
 *
 *   1. Un reintento a los 1,5 s. Desde un hospital o desde Venezuela un chunk se cae por un
 *      corte de red de un segundo, no porque la versión cambió; recargar por eso le haría perder
 *      el lugar al médico. webpack 5 vuelve a pedir el script en el segundo `import()`.
 *   2. Si vuelve a fallar y es un chunk viejo (hubo deploy con la pestaña abierta), se delega en
 *      `manejarErrorDeChunk`: guarda lo pendiente y recarga UNA vez (su marca de 60 s corta el
 *      loop). Mientras recarga, la promesa queda pendiente y el `Suspense` sigue mostrando la
 *      carga. Si no corresponde recargar (sin red, o ya recargó hace poco), el error sube al
 *      `ErrorBoundary`, que ofrece "Recargar".
 * Cubre R3, R4, R6 — .claude/specs/1717-velocidad/
 */
export const ESPERA_REINTENTO_MS = 1500;

// Cuándo un chunk que no bajó recarga la página: SOLO si se confirma que hubo un deploy (el
// index.html servido apunta a otro main que el de esta pestaña, `comprobarVersion`). Sin red, o con
// una red que el navegador cree buena pero no sale (wifi sin salida, portal cautivo: ahí
// `navigator.onLine` sigue en true), recargar deja al médico en la página de error del navegador y
// sin el Turno (F6 del revisor v2, F7 del revisor v3). En esos casos falla la parte (su límite
// avisa) o la vista (el ErrorBoundary ofrece "Recargar"), y nada se recarga solo: la regla de
// version-nueva.js es que la app nunca se recarga sola salvo por una versión nueva.
// `recargar: false` = nunca recarga, ni con deploy: para lo que vive en un formulario a medio
// llenar (los Términos del registro: recargar le borraría al lead lo escrito, F8 del revisor v3).
const sinConexion = () => {
  try {
    return typeof navigator !== "undefined" && navigator.onLine === false;
  } catch (_) {
    return false;
  }
};

export function importarConReintento(
  importar,
  {
    esperaMs = ESPERA_REINTENTO_MS,
    manejar = manejarErrorDeChunk,
    recargar = true,
    offline = sinConexion,
    hayVersionNueva = () => comprobarVersion(),
  } = {},
) {
  return importar().catch(() =>
    new Promise((resolve) => setTimeout(resolve, esperaMs))
      .then(() => importar())
      .catch(async (err) => {
        if (!recargar || offline() || !esErrorDeChunk(err)) throw err;
        if (!(await hayVersionNueva())) throw err;
        if (await manejar(err)) {
          return new Promise(() => {});
        }
        throw err;
      }),
  );
}

/**
 * `React.lazy` guarda para siempre una carga fallida: después de un corte de red la vista
 * quedaría rota hasta recargar. Cada fallo deja anotado cómo renovarse, y lo renueva el límite de
 * error que lo atrapó (`LimiteParte` o el `ErrorBoundary` de las rutas), cuando esa parte ya no se
 * está dibujando. Renovar en el acto del fallo haría que React volviera a montarla y reintentara
 * sin fin mientras no hay red (lo encontró el test de abajo). Así, el próximo intento real
 * (volver a abrir la planilla, volver a la ruta) baja el chunk de nuevo, sin loop.
 */
const pendientesDeRenovar = new Set();

export function renovarDiferidosFallidos() {
  pendientesDeRenovar.forEach((renovar) => renovar());
  pendientesDeRenovar.clear();
}

export function lazyConReintento(importar, opts) {
  let actual;
  const crear = () =>
    React.lazy(() =>
      importarConReintento(importar, opts).catch((err) => {
        pendientesDeRenovar.add(() => {
          actual = crear();
        });
        throw err;
      }),
    );
  actual = crear();
  const Diferido = React.forwardRef((props, ref) => {
    const Comp = actual;
    return <Comp ref={ref} {...props} />;
  });
  return Diferido;
}

/**
 * Límite de error de UNA parte diferida (#1717 F1 del revisor v1). Sin esto, si la planilla o los
 * gráficos no bajaban (sin red un momento, o en 3G donde no hay precarga), el error subía al
 * boundary de las rutas y se caía el Turno entero. Acá el fallo queda en esa parte: el padre
 * decide qué hacer (`onFallo`) y la parte no dibuja nada.
 */
export class LimiteParte extends React.Component {
  constructor(props) {
    super(props);
    this.state = { fallo: false };
  }

  static getDerivedStateFromError() {
    return { fallo: true };
  }

  componentDidCatch(error) {
    renovarDiferidosFallidos();
    try {
      this.props.onFallo?.(error);
    } catch (_) {
      /* el aviso no puede romper el Turno */
    }
  }

  render() {
    return this.state.fallo ? null : this.props.children;
  }
}

/**
 * Baja en segundo plano, con la app quieta, los chunks que el médico probablemente va a abrir
 * (planilla de indicaciones, gráficos de exámenes): el arranque queda liviano y el clic después
 * es instantáneo porque el chunk ya está en caché. NO precarga con "ahorro de datos" ni en
 * conexiones 2G/3G: ahí el médico paga cada MB y lo que no abra no se descarga.
 * Devuelve la función que cancela (para el cleanup del efecto).
 */
export function precargarEnReposo(importadores, { despuesMs = 8000, nav = typeof navigator !== "undefined" ? navigator : null } = {}) {
  const conexion = nav && nav.connection;
  if (conexion && (conexion.saveData || /(^|-)(2g|3g)$/.test(String(conexion.effectiveType || "")))) {
    return () => {};
  }
  let cancelado = false;
  const timer = setTimeout(() => {
    const correr = () => {
      if (cancelado) return;
      importadores.forEach((importar) => {
        try {
          importar().catch(() => {});
        } catch (_) {
          /* precarga: si falla, se baja al abrir */
        }
      });
    };
    if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(correr, { timeout: 5000 });
    } else {
      correr();
    }
  }, despuesMs);
  return () => {
    cancelado = true;
    clearTimeout(timer);
  };
}
